import { LOGIN_LOGS_STORE } from "@/constants/sessionStorage";
import { rawDefaultStateValuesGetter, zustandStorage } from "@/lib/storage";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import { create } from "zustand";
import { persist } from "zustand/middleware";

const loginsStoreSchema = z.object({
  performByUserId: z.int().optional(),
  targetLoginId: z.int().optional(),

  actions: z.array(z.string()).optional(),

  minCreatedAt: z.string().optional(),
  maxCreatedAt: z.string().optional(),

  sortBy: z.string().optional(),
  orderBy: z.string().optional(),
  page: z.number().min(1),
  pageSize: z.number().max(200),
});

export type LoginsStore = z.infer<typeof loginsStoreSchema>;

const defaults: LoginsStore = {
  page: 1,
  pageSize: 20,
};

interface Store {
  state: LoginsStore;
  setState: (newState: Partial<LoginsStore>) => void;
  reset: () => void;
}

const getRawDefaultValues = rawDefaultStateValuesGetter(LOGIN_LOGS_STORE);

const { data: defaultValues } = loginsStoreSchema.safeParse(getRawDefaultValues());

const useStore = create<Store>()(
  persist(
    (set) => ({
      state: defaultValues ?? defaults,

      setState: (newState) =>
        set((s) => {
          const merged = { ...s.state, ...newState };
          const parsed = loginsStoreSchema.safeParse(merged);
          return {
            state: parsed.success ? parsed.data : s.state,
          };
        }),

      reset: () => set({ state: defaults }),
    }),
    {
      name: LOGIN_LOGS_STORE,
      onRehydrateStorage: () => (state) => {
        if (!state?.state) return;

        const parsed = loginsStoreSchema.safeParse(state.state);

        if (!parsed.success) {
          state.state = defaults;
          return;
        }

        state.state = parsed.data;
      },
      partialize: (state) => state.state,
      storage: zustandStorage,
    },
  ),
);

export const useLoginLogsState = () => {
  const store = useStore();
  const [params] = useSearchParams();
  const paramsValue = params.get(LOGIN_LOGS_STORE);

  useEffect(() => {
    if (!paramsValue) {
      store.reset();
    }
  }, [paramsValue]);

  return store;
};
